'use client'

import { useState, useRef } from 'react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { newWin } from '@/lib/utils'
import {
  Gift,
  Twitter,
  Linkedin,
  ArrowRight,
  Loader2,
  Check,
  X,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useShareSendCoins } from '@/components/modals/share-send-coins/use-share-send-coins'
import { TwitterShareButton, LinkedinShareButton } from 'react-share'
import { SHARE_COIN } from '@/lib/constant'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useTranslations } from 'next-intl'
import { useClickAway } from 'react-use'
import type { SharePlatform } from './use-share-send-coins'
import { RedNoteIcon } from './red-note'

interface ShareButtonProps {
  platform: SharePlatform
  isLoading: SharePlatform | null
  onShare: (platform: SharePlatform) => void
  icon: React.ReactNode
  color: string
  label: string
}

const ShareButton: React.FC<ShareButtonProps> = ({
  platform,
  isLoading,
  onShare,
  icon,
  color,
  label,
}) => (
  <button
    type="button"
    className={`flex-1 flex flex-col items-center justify-center p-8 rounded-2xl border-none cursor-pointer transition-all duration-300 max-w-[140px] shadow-[0_3px_10px_rgba(0,0,0,0.08)] dark:shadow-[0_3px_10px_rgba(0,0,0,0.2)] hover:translate-y-[-5px] hover:shadow-[0_10px_20px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_10px_20px_rgba(0,0,0,0.3)] hover:bg-[rgba(${color},0.1)] dark:hover:bg-[rgba(${color},0.2)] relative disabled:opacity-50 disabled:cursor-not-allowed`}
    onClick={() => !isLoading && onShare(platform)}
    disabled={isLoading !== null}
  >
    {isLoading === platform ? (
      <Loader2
        className="h-5 w-5 animate-spin"
        style={{ color: `#${color}` }}
      />
    ) : (
      icon
    )}
    <span className="mt-3 font-semibold" style={{ color: `#${color}` }}>
      {label}
    </span>
  </button>
)

const PLATFORM_CONFIGS: Record<
  SharePlatform,
  { icon: React.ReactNode; color: string; label: string }
> = {
  twitter: {
    icon: <Twitter className="h-5 w-5 text-[#1DA1F2]" />,
    color: '1DA1F2',
    label: 'Twitter',
  },
  linkedin: {
    icon: <Linkedin className="h-5 w-5 text-[#0077B5]" />,
    color: '0077B5',
    label: 'LinkedIn',
  },
  xiaohongshu: {
    icon: <RedNoteIcon className="h-5 w-5 text-[#FF3366]" />,
    color: 'FF3366',
    label: 'RedNote',
  },
}

export default function ShareSendCoinsModal({
  onClose,
  onGotIt,
  onShareSuccess,
  onShareError,
  onClickAway,
}: {
  onClose?: (error: string | null) => void
  onGotIt?: (error: string | null) => void
  onShareSuccess?: () => void
  onShareError?: (error: string) => void
  onClickAway?: () => void
}) {
  const {
    step,
    selectedPlatform,
    verificationUrl,
    isVerifying,
    error,
    setVerificationUrl,
    handleSelectPlatform,
    handleVerify,
    shareContent,
    isLoadingContent,
    reset,
    setStep,
    log,
    publishUrl,
  } = useShareSendCoins({
    onShareSuccess,
    onShareError,
  })

  const { data: user } = useCurrentUser()
  const t = useTranslations('share-send-coins')

  const ref = useRef(null)
  useClickAway(ref, () => {
    onClickAway?.()
  })

  return (
    <DialogContent
      ref={ref}
      className="sm:max-w-[500px] p-0 gap-0 border-none  sm:rounded-xl"
    >
      <DialogHeader className="hidden">
        <DialogTitle />
      </DialogHeader>

      <div className="bg-gradient-to-r from-blue-600 to-purple-500 p-6 rounded-t-xl text-white relative">
        {/* <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={close}
          >
            <X className="h-4 w-4" />
          </Button>
        </div> */}
        <div className="absolute top-4 left-4 bg-white/20 px-3 py-1 rounded-full flex items-center gap-1">
          <Gift className="h-4 w-4" />
        </div>

        <div className="text-center mt-4">
          <h2 className="text-2xl font-bold mb-2">{t('title')}</h2>
          <div className="flex items-center justify-center gap-2">
            <span className="text-5xl font-bold">{SHARE_COIN}</span>
            <Gift className="h-8 w-8 [animation:custom-bounce_1s_infinite]" />
          </div>
        </div>
        <p className="text-sm text-gray-200 dark:text-white/60 text-center">
          {log}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="px-6 py-4">
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div
            className="h-2 bg-gradient-to-r from-blue-600 to-purple-500 rounded-full transition-all duration-300"
            style={{ width: `${step * 33.33}%` }}
          />
        </div>
        <div className="flex justify-between mt-2">
          {[1, 2, 3].map((num) => (
            <div
              key={num}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center border transition-colors',
                step >= num
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400',
              )}
            >
              {num}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <StepOne
              onSelectPlatform={handleSelectPlatform}
              shareContent={shareContent}
              publishUrl={publishUrl}
            />
          )}

          {step === 2 && (
            <StepTwo
              selectedPlatform={selectedPlatform}
              verificationUrl={verificationUrl}
              setVerificationUrl={setVerificationUrl}
              isVerifying={isVerifying}
              error={error}
              onVerify={handleVerify}
              onChange={() => {
                setStep(1)
              }}
            />
          )}

          {step === 3 && (
            <StepThree
              userCredits={(user?.coin || 0).toFixed(2)}
              onClose={() => {
                onClose?.(error)
              }}
              onGotIt={() => {
                onGotIt?.(error)
              }}
              error={error}
              onRetry={() => {
                reset()
              }}
              publishUrl={publishUrl}
            />
          )}
        </AnimatePresence>
      </div>

      <style jsx global>{`
        @keyframes custom-bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-25%);
          }
        }
      `}</style>
    </DialogContent>
  )
}

// Step Components...
function StepOne({
  onSelectPlatform,
  shareContent,
  publishUrl,
}: {
  onSelectPlatform: (platform: SharePlatform) => void
  shareContent: string
  publishUrl: string
}) {
  const [isLoading, setIsLoading] = useState<SharePlatform | null>(null)
  const hasShared = useRef<boolean>(false)
  const t = useTranslations('share-send-coins')

  const handleShare = async (platform: SharePlatform) => {
    if (hasShared.current) return

    try {
      setIsLoading(platform)
      await onSelectPlatform(platform)
      hasShared.current = true
    } catch (err) {
      setIsLoading(null)
      hasShared.current = false
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center"
    >
      <h3 className="text-xl font-semibold mb-4 gradient-text">
        {t('share_description', { SHARE_COIN: SHARE_COIN })}
      </h3>
      <p className="text-gray-600 dark:text-gray-300 mb-2">
        {t('share_description_note')}
      </p>

      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-6">
        {t('share_description_note_2')}
      </p>

      {/* Hidden Share Buttons */}
      <div className="hidden">
        <TwitterShareButton
          id="twitter-share-button"
          title={shareContent || t('share_description_note_3')}
          hashtags={['AI', 'heybossAI']}
          via="heybossAI"
          related={['heybossAI']}
          url={publishUrl}
          openShareDialogOnClick={false}
          onClick={(_, link) => {
            newWin(link, 'twitter-share-button')
          }}
        >
          <div />
        </TwitterShareButton>

        <LinkedinShareButton
          id="linkedin-share-button"
          title={shareContent || t('share_description_note_3')}
          summary={shareContent || t('share_description_note_3')}
          source="heybossAI"
          url={publishUrl}
          openShareDialogOnClick={false}
          onClick={(_, link) => {
            newWin(link, 'linkedin-share-button')
          }}
        >
          <div />
        </LinkedinShareButton>
      </div>

      {/* Visible Share Buttons */}
      <div className="flex gap-4 justify-center">
        {Object.entries(PLATFORM_CONFIGS).map(([platform, config]) => (
          <ShareButton
            key={platform}
            platform={platform as SharePlatform}
            isLoading={isLoading}
            onShare={handleShare}
            icon={config.icon}
            color={config.color}
            label={config.label}
          />
        ))}
      </div>
    </motion.div>
  )
}

function StepTwo({
  selectedPlatform,
  verificationUrl,
  setVerificationUrl,
  isVerifying,
  error,
  onVerify,
  onChange,
}: {
  selectedPlatform: SharePlatform | null
  verificationUrl: string
  setVerificationUrl: (url: string) => void
  isVerifying: boolean
  error: string | null
  onVerify: () => void
  onChange: () => void
}) {
  const t = useTranslations('share-send-coins')

  const getPlatformIcon = (platform: SharePlatform) => {
    switch (platform) {
      case 'twitter':
        return <Twitter size={20} />
      case 'linkedin':
        return <Linkedin size={20} />
      case 'xiaohongshu':
        return <RedNoteIcon className="h-5 w-5" />
    }
  }

  const getPlatformName = (platform: SharePlatform) => {
    return PLATFORM_CONFIGS[platform].label
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center"
    >
      <h3 className="text-xl font-semibold mb-4 gradient-text">
        {t('almost_there')}
      </h3>
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        {t('verify_description')}
      </p>

      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder={t('paste_url', {
              platform: selectedPlatform
                ? getPlatformName(selectedPlatform)
                : '',
            })}
            value={verificationUrl}
            onChange={(e) => setVerificationUrl(e.target.value)}
            className={error ? 'border-red-500' : ''}
          />
          <Button onClick={onVerify} disabled={isVerifying || !verificationUrl}>
            {isVerifying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {t('verify')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {error && <p className="text-sm text-red-500 text-left">{error}</p>}
      </div>

      {selectedPlatform && (
        <div className="flex items-center justify-center gap-2.5 mt-5 p-2.5 bg-[#f9f9f9] dark:bg-gray-800 rounded-xl">
          <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {getPlatformIcon(selectedPlatform)}
          </div>
          <p>
            {t('shared_on')}{' '}
            <strong>{getPlatformName(selectedPlatform)}</strong>
            <button
              type="button"
              className="bg-transparent border-none text-blue-600 font-semibold cursor-pointer text-sm pl-2 underline"
              onClick={onChange}
            >
              {t('change')}
            </button>
          </p>
        </div>
      )}
    </motion.div>
  )
}

function StepThree({
  userCredits,
  onClose,
  onGotIt,
  error,
  onRetry,
  publishUrl,
}: {
  userCredits: string
  onClose: () => void
  onGotIt: () => void
  error?: string | null
  onRetry: () => void
  publishUrl: string
}) {
  const t = useTranslations('share-send-coins')

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="text-center"
      >
        <div className="mb-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-red-600 dark:text-red-400">
            Verification Failed
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mt-2">{error}</p>
        </div>

        <div className="flex gap-2 justify-center">
          <button
            onClick={onRetry}
            className="bg-blue-600 text-white px-8 py-3.5 rounded-full font-semibold text-base cursor-pointer hover:bg-blue-700 dark:hover:bg-blue-500 transition-all duration-300"
            type="button"
          >
            {t('try_again')}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-8 py-3.5 rounded-full font-semibold text-base cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-300"
            type="button"
          >
            {t('close')}
          </button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center"
    >
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold gradient-text">
          {t('congratulations')}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mt-2">
          {t('success_message', { credits: SHARE_COIN })}
        </p>
      </div>

      <div className="flex items-center justify-center gap-2 mb-6">
        <span className="text-gray-600 dark:text-gray-300">
          {t('your_credits')}
        </span>
        <div className="bg-gradient-to-r from-blue-600 to-purple-500 text-white px-3 py-1 rounded-full flex items-center gap-1">
          <span>{userCredits}</span>
          <Gift className="h-4 w-4" />
        </div>
      </div>

      <button
        onClick={onGotIt}
        className="animate-pulse bg-gradient-to-r from-blue-600 to-purple-500 text-white px-8 py-3.5 rounded-full font-semibold text-base cursor-pointer hover:scale-105 shadow-lg hover:shadow-xl transition-all duration-300"
        type="button"
      >
        {t('got_it')}
      </button>
    </motion.div>
  )
}
